// Page Visibility Manager
// Handles page visibility changes and manages background operations

import serviceWorkerManager from './serviceWorkerManager';

class PageVisibilityManager {
  constructor() {
    this.isVisible = !document.hidden;
    this.visibilityChangeHandlers = new Set();
    this.backgroundTasks = new Map();
    this.init();
  }

  init() {
    // Listen for visibility changes
    document.addEventListener('visibilitychange', this.handleVisibilityChange.bind(this));
    
    // Listen for page unload
    window.addEventListener('beforeunload', this.handlePageUnload.bind(this));
    
    // Listen for page focus/blur
    window.addEventListener('focus', this.handlePageFocus.bind(this));
    window.addEventListener('blur', this.handlePageBlur.bind(this));
    
    console.log('Page Visibility Manager initialized');
  }

  handleVisibilityChange() {
    const wasVisible = this.isVisible;
    this.isVisible = !document.hidden;
    
    console.log(`Page visibility changed: ${this.isVisible ? 'visible' : 'hidden'}`);
    
    if (wasVisible && !this.isVisible) {
      // Page became hidden
      this.onPageHidden();
    } else if (!wasVisible && this.isVisible) {
      // Page became visible
      this.onPageVisible();
    }
    
    // Notify all handlers
    this.visibilityChangeHandlers.forEach(handler => {
      try {
        handler(this.isVisible);
      } catch (error) {
        console.error('Error in visibility change handler:', error);
      }
    });
  }

  handlePageFocus() {
    console.log('Page gained focus');
    this.onPageVisible();
  }

  handlePageBlur() {
    console.log('Page lost focus');
    this.onPageHidden();
  }

  handlePageUnload() {
    console.log('Page is unloading, transferring tasks to service worker');
    this.transferAllTasksToServiceWorker();
  }

  onPageHidden() {
    console.log('Page is now hidden, transferring active tasks to service worker');
    this.transferAllTasksToServiceWorker();
  }

  onPageVisible() {
    console.log('Page is now visible, checking for completed background tasks');
    this.checkBackgroundTaskStatus();
  }

  // Register a task that should continue in background
  registerBackgroundTask(taskId, taskType, taskData) {
    this.backgroundTasks.set(taskId, {
      type: taskType,
      data: taskData,
      startTime: Date.now(),
      status: 'active'
    });
    
    console.log(`Registered background task: ${taskId} (${taskType})`);
  }

  // Unregister a completed task
  unregisterBackgroundTask(taskId) {
    if (this.backgroundTasks.has(taskId)) {
      this.backgroundTasks.delete(taskId);
      console.log(`Unregistered background task: ${taskId}`);
    }
  }

  // Transfer all active tasks to service worker
  transferAllTasksToServiceWorker() {
    if (!serviceWorkerManager.isAvailable()) {
      console.warn('Service worker not available for background task transfer');
      return;
    }

    this.backgroundTasks.forEach((task, taskId) => {
      if (task.status === 'active') {
        this.transferTaskToServiceWorker(taskId, task);
      }
    });
  }

  // Transfer specific task to service worker
  transferTaskToServiceWorker(taskId, task) {
    if (!serviceWorkerManager.isAvailable()) {
      console.warn(`Cannot transfer task ${taskId}: Service worker not available`);
      return;
    }

    try {
      switch (task.type) {
        case 'email_verification':
          // Email verification is already handled by service worker
          console.log(`Email verification task ${taskId} already running in service worker`);
          break;
          
        case 'website_analysis':
          // Transfer website analysis to service worker
          serviceWorkerManager.startWebsiteAnalysis(
            task.data.websites,
            task.data.config,
            task.data.serviceOptions,
            task.data.scrapingOptions,
            (progress) => {
              // Store progress for when page becomes visible
              this.updateTaskProgress(taskId, progress);
            }
          );
          console.log(`Transferred website analysis task ${taskId} to service worker`);
          break;
          
        default:
          console.warn(`Unknown task type for background transfer: ${task.type}`);
      }
    } catch (error) {
      console.error(`Failed to transfer task ${taskId} to service worker:`, error);
    }
  }

  // Check status of background tasks when page becomes visible
  async checkBackgroundTaskStatus() {
    if (!serviceWorkerManager.isAvailable()) {
      return;
    }

    const taskPromises = Array.from(this.backgroundTasks.keys()).map(async (taskId) => {
      try {
        const status = await serviceWorkerManager.getTaskStatus(taskId);
        if (status && status.task) {
          this.updateTaskProgress(taskId, status.task);
        }
      } catch (error) {
        console.error(`Failed to check status for task ${taskId}:`, error);
      }
    });

    await Promise.all(taskPromises);
  }

  // Update task progress
  updateTaskProgress(taskId, progress) {
    const task = this.backgroundTasks.get(taskId);
    if (task) {
      task.lastProgress = progress;
      task.lastUpdate = Date.now();
      
      if (progress.status === 'completed' || progress.status === 'error') {
        task.status = progress.status;
      }
    }
  }

  // Add visibility change handler
  addVisibilityChangeHandler(handler) {
    this.visibilityChangeHandlers.add(handler);
    
    // Return cleanup function
    return () => {
      this.visibilityChangeHandlers.delete(handler);
    };
  }

  // Get current visibility state
  isPageVisible() {
    return this.isVisible;
  }

  // Get all background tasks
  getBackgroundTasks() {
    return Array.from(this.backgroundTasks.entries()).map(([taskId, task]) => ({
      taskId,
      ...task
    }));
  }

  // Get active background tasks
  getActiveBackgroundTasks() {
    return this.getBackgroundTasks().filter(task => task.status === 'active');
  }

  // Check if there are any active background tasks
  hasActiveBackgroundTasks() {
    return this.getActiveBackgroundTasks().length > 0;
  }

  // Show notification about background tasks (if permission granted)
  async showBackgroundTaskNotification(message) {
    if ('Notification' in window && Notification.permission === 'granted') {
      try {
        new Notification('KnowToConnect', {
          body: message,
          icon: '/favicon.ico',
          badge: '/favicon.ico',
          tag: 'background-task'
        });
      } catch (error) {
        console.error('Failed to show notification:', error);
      }
    }
  }

  // Request notification permission
  async requestNotificationPermission() {
    if ('Notification' in window && Notification.permission === 'default') {
      try {
        const permission = await Notification.requestPermission();
        console.log(`Notification permission: ${permission}`);
        return permission === 'granted';
      } catch (error) {
        console.error('Failed to request notification permission:', error);
        return false;
      }
    }
    return Notification.permission === 'granted';
  }

  // Cleanup
  destroy() {
    document.removeEventListener('visibilitychange', this.handleVisibilityChange.bind(this));
    window.removeEventListener('beforeunload', this.handlePageUnload.bind(this));
    window.removeEventListener('focus', this.handlePageFocus.bind(this));
    window.removeEventListener('blur', this.handlePageBlur.bind(this));
    
    this.visibilityChangeHandlers.clear();
    this.backgroundTasks.clear();
    
    console.log('Page Visibility Manager destroyed');
  }
}

// Create singleton instance
const pageVisibilityManager = new PageVisibilityManager();

// Auto-request notification permission
if (typeof window !== 'undefined') {
  pageVisibilityManager.requestNotificationPermission();
}

export default pageVisibilityManager;